import {
  AHSeparator,
  AHText,
  Box,
  ListItem,
  useLayoutContext,
  useTheme,
} from "arkh";
import React, { useCallback, useState } from "react";
import { RoutineScheduleMonthly } from "../RoutineEditNavigation";
import { MonthSelector } from "../MonthSelector";
import { Picker } from "@react-native-picker/picker";
import { useIntlFormattedMonthDay } from "../../../Team/TeamHomeNavigator/TeamScheduleListItem/useIntlFormattedMonthDay";
import { FormattedMessage } from "react-intl";
import { Platform } from "react-native";

const getDayOrdinal = (day: number) => {
  return day < 0 ? 31 + day : day;
};

export const RoutineEditScheduleMonthlyOptions = React.memo(
  RoutineEditScheduleMonthlyOptionsC
);
function RoutineEditScheduleMonthlyOptionsC(props: {
  months: number[];
  startDay: number;
  endDay: number;
  setMonthlyParams(_: Partial<RoutineScheduleMonthly>): void;
}) {
  const onSetEndDay = useCallback(
    (endDay: number) => {
      if (getDayOrdinal(props.startDay) > getDayOrdinal(endDay)) {
        props.setMonthlyParams({
          startDay: endDay,
          endDay,
        });
      } else {
        props.setMonthlyParams({ endDay });
      }
    },
    [props]
  );

  const onSetStartDay = useCallback(
    (startDay: number) => {
      if (getDayOrdinal(startDay) > getDayOrdinal(props.endDay)) {
        props.setMonthlyParams({
          startDay,
          endDay: startDay,
        });
      } else {
        props.setMonthlyParams({ startDay });
      }
    },
    [props]
  );
  const onPressMonth = useCallback(
    (month: number) => {
      if (props.months.includes(month)) {
        props.setMonthlyParams({
          months: props.months.filter((m) => m !== month),
        });
      } else {
        props.setMonthlyParams({ months: [...props.months, month] });
      }
    },
    [props]
  );

  return (
    <>
      {Platform.OS === "web" ? (
        <DaySelectorWeb
          startDay={props.startDay}
          endDay={props.endDay}
          onSetStartDay={onSetStartDay}
          onSetEndDay={onSetEndDay}
        />
      ) : (
        <DaySelectorNative
          startDay={props.startDay}
          endDay={props.endDay}
          onSetStartDay={onSetStartDay}
          onSetEndDay={onSetEndDay}
        />
      )}
      <AHSeparator />
      <Box space="medium">
        <AHText type="detail" spaceBottom="medium">
          <FormattedMessage
            defaultMessage="Months"
            description="RoutineEditScheduleMonthlyOptions months title"
          />
          :
        </AHText>
        <MonthSelector
          selectedMonths={props.months}
          onPressMonth={onPressMonth}
        />
      </Box>
    </>
  );
}

function DaySelectorWeb(props: {
  startDay: number;
  onSetStartDay(_: number): void;
  endDay: number;
  onSetEndDay(_: number): void;
}) {
  return (
    <>
      <ListItem
        right={
          <MonthdayPickerWeb
            onValueChange={props.onSetStartDay}
            selectedValue={props.startDay}
          />
        }
      >
        <ListItem.Title>
          <FormattedMessage
            defaultMessage="Starting date"
            description="RoutineEditScheduleMonthlyOptions starting date title"
          />
        </ListItem.Title>
      </ListItem>
      <AHSeparator />
      <ListItem
        right={
          <MonthdayPickerWeb
            onValueChange={props.onSetEndDay}
            selectedValue={props.endDay}
          />
        }
      >
        <ListItem.Title>
          <AHText type="list-item-title">
            <FormattedMessage
              defaultMessage="End date"
              description="RoutineEditScheduleMonthlyOptions end date title"
            />
          </AHText>
        </ListItem.Title>
      </ListItem>
    </>
  );
}

function DaySelectorNative(props: {
  startDay: number;
  onSetStartDay(_: number): void;
  endDay: number;
  onSetEndDay(_: number): void;
}) {
  const [startDayExpanded, setStartDayExpanded] = useState(false);
  const [endDayExpanded, setEndDayExpanded] = useState(false);

  const toggleStartDayExpanded = useCallback(() => {
    setStartDayExpanded((v) => !v);
  }, []);
  const toggleEndDayExpanded = useCallback(() => {
    setEndDayExpanded((v) => !v);
  }, []);

  return (
    <>
      <ListItem
        onPress={toggleStartDayExpanded}
        right={
          <DayCollapsed day={props.startDay} expanded={startDayExpanded} />
        }
      >
        <ListItem.Title>
          <FormattedMessage
            defaultMessage="Starting date"
            description="RoutineEditScheduleMonthlyOptions starting date title"
          />
        </ListItem.Title>
      </ListItem>
      {startDayExpanded ? (
        <>
          <AHSeparator />
          <MonthdayPicker
            onValueChange={props.onSetStartDay}
            selectedValue={props.startDay}
          />
        </>
      ) : null}
      <AHSeparator />
      <ListItem
        onPress={toggleEndDayExpanded}
        right={<DayCollapsed day={props.endDay} expanded={endDayExpanded} />}
      >
        <ListItem.Title>
          <AHText type="list-item-title">
            <FormattedMessage
              defaultMessage="End date"
              description="RoutineEditScheduleMonthlyOptions end date title"
            />
          </AHText>
        </ListItem.Title>
      </ListItem>
      {endDayExpanded ? (
        <>
          <AHSeparator />
          <MonthdayPicker
            onValueChange={props.onSetEndDay}
            selectedValue={props.endDay}
          />
        </>
      ) : null}
    </>
  );
}
function DayCollapsed(props: { day: number; expanded: boolean }) {
  const formattedOrdinal = useIntlFormattedMonthDay("short");
  return (
    <Box
      cornerRadius="medium"
      color="surface-backgroundControl"
      spaceVertical="small"
      style={{
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        width: 80,
        justifyContent: "center",
      }}
    >
      <AHText type="list-item-title">{formattedOrdinal(props.day)}</AHText>
    </Box>
  );
}

const mondayOptions = [
  ...Array(28)
    .fill(null)
    .map((_, i) => i + 1),
  -2,
  -1,
];

function MonthdayPicker(props: {
  onValueChange(_: number): void;
  selectedValue: number;
}) {
  const formattedOrdinal = useIntlFormattedMonthDay();

  return (
    <Picker
      onValueChange={(val) =>
        props.onValueChange(typeof val === "string" ? parseInt(val, 10) : val)
      }
      selectedValue={props.selectedValue}
    >
      {mondayOptions.map((v) => {
        return <Picker.Item key={v} label={formattedOrdinal(v)} value={v} />;
      })}
    </Picker>
  );
}
function MonthdayPickerWeb(props: {
  onValueChange(_: number): void;
  selectedValue: number;
}) {
  const formattedOrdinal = useIntlFormattedMonthDay();

  const layout = useLayoutContext();
  const theme = useTheme();
  return (
    <Picker
      style={{
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        width: 190,
        justifyContent: "center",
        backgroundColor: theme["surface-backgroundControl"],
        paddingVertical: layout.space.small,
        borderRadius: layout.radius.medium,
        paddingHorizontal: layout.space.small,
      }}
      onValueChange={(val) =>
        props.onValueChange(typeof val === "string" ? parseInt(val, 10) : val)
      }
      selectedValue={props.selectedValue}
    >
      {mondayOptions.map((v) => {
        return <Picker.Item key={v} label={formattedOrdinal(v)} value={v} />;
      })}
    </Picker>
  );
}
